import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, Trash2, Sparkles, PlusCircle, Package } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatIDR, formatIDRShort, clientName, formatApiError } from "../lib/format";
import { DIVISIONS, QUOTATION_STATUS_LABEL, divisionById } from "../lib/constants";

const emptyLine = () => ({ description: "", quantity: 1, unit_price: 0, source: "manual", division_id: "" });
const emptyForm = {
  client_id: "", event_name: "", event_date: "", venue: "", location: "",
  division_ids: [], lines: [], discount: 0, valid_until: "", terms: "", notes: "",
};

export default function Quotations() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  // Modal: Auto-fill Paket DWG (bundling=true)
  const [pkgOpen, setPkgOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState({});
  const [targetPrice, setTargetPrice] = useState("");

  // Modal: Add-on (bundling=false)
  const [addonOpen, setAddonOpen] = useState(false);
  const [addonItems, setAddonItems] = useState([]);
  const [addonSearch, setAddonSearch] = useState("");
  const [addonDiv, setAddonDiv] = useState("all");
  const [selectedAddon, setSelectedAddon] = useState({});

  // Per-division pricelists (cached)
  const [divPricelist, setDivPricelist] = useState({}); // {divisionId: [products]}

  const load = () => api.get("/quotations").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
  }, []);

  const subtotal = form.lines.reduce((a, l) => a + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const total = Math.max(subtotal - Number(form.discount || 0), 0);

  // Deteksi package mode: sudah ada line dari paket DWG bundle
  const packageMode = useMemo(() => form.lines.some((l) => l.source === "dwg_package"), [form.lines]);

  const toggleDiv = (id) => {
    setForm((f) => {
      const active = f.division_ids.includes(id);
      const next = active ? f.division_ids.filter((x) => x !== id) : [...f.division_ids, id];
      return { ...f, division_ids: next };
    });
    // Prefetch pricelist untuk divisi ini kalau belum ada
    if (!divPricelist[id]) fetchDivPricelist(id);
  };

  const fetchDivPricelist = async (divisionId) => {
    try {
      const { data } = await api.get(`/master-products?division=${divisionId}&bundling=false&status=PUBLISHED`);
      setDivPricelist((prev) => ({ ...prev, [divisionId]: data }));
    } catch (e) { /* ignore */ }
  };

  const setLine = (i, k, v) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [k]: v };
    setForm({ ...form, lines });
  };
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) });

  const addManualLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });

  const addPricelistItem = (product, divisionId) => {
    const price = Number(product.current_price || product.promo_price || product.normal_price || 0);
    const divShort = divisionById(divisionId)?.short || divisionId;
    const line = {
      description: `[${divShort}] ${product.product_name}${product.short_description ? ` — ${product.short_description}` : ""}`,
      quantity: 1, unit_price: price,
      source: "division_item", division_id: divisionId, product_id: product.product_id,
    };
    setForm((f) => ({ ...f, lines: [...f.lines, line] }));
    toast.success(`+ ${product.product_name}`);
  };

  // --- Auto-fill Paket DWG ---
  const openPkgModal = async () => {
    setPkgOpen(true);
    setSelectedPkg({});
    try {
      const params = new URLSearchParams({ bundling: "true", status: "PUBLISHED" });
      if (targetPrice) params.set("similar_to", String(Number(targetPrice)));
      const { data } = await api.get(`/master-products?${params.toString()}`);
      setPackages(data);
    } catch (e) { toast.error("Gagal memuat paket DWG"); }
  };

  const applyPackage = () => {
    const chosen = packages.filter((p) => selectedPkg[p.product_id || p.id]);
    if (chosen.length === 0) { toast.error("Pilih minimal 1 paket"); return; }
    const newLines = [];
    const allDivs = [];
    chosen.forEach((p) => {
      const totalPrice = Number(p.current_price || p.promo_price || p.normal_price || 0);
      const inclDivs = (p.included_divisions && p.included_divisions.length ? p.included_divisions : (p.division ? [p.division] : []));
      allDivs.push(...inclDivs);
      newLines.push({
        description: `${p.product_name}${p.short_description ? ` — ${p.short_description}` : ""}`,
        quantity: 1, unit_price: totalPrice,
        source: "dwg_package", product_id: p.product_id, division_id: "",
        included_divisions: inclDivs,
      });
    });
    const mergedDivs = Array.from(new Set([...form.division_ids, ...allDivs]));
    setForm((f) => ({ ...f, lines: [...f.lines, ...newLines], division_ids: mergedDivs }));
    // Prefetch pricelists (walau tersembunyi di package mode, siapkan kalau user hapus paket)
    mergedDivs.forEach((d) => { if (!divPricelist[d]) fetchDivPricelist(d); });
    setPkgOpen(false);
    toast.success(`${chosen.length} paket DWG ditambahkan · vendor auto-terisi`);
  };

  // --- Add-on Modal ---
  const openAddonModal = async () => {
    setAddonOpen(true);
    setSelectedAddon({});
    setAddonSearch("");
    setAddonDiv("all");
    try {
      const { data } = await api.get(`/master-products?bundling=false&status=PUBLISHED`);
      setAddonItems(data);
    } catch (e) { toast.error("Gagal memuat pricelist add-on"); }
  };

  const filteredAddons = useMemo(() => {
    return addonItems.filter((p) => {
      if (addonDiv !== "all" && p.division !== addonDiv) return false;
      if (addonSearch) {
        const q = addonSearch.toLowerCase();
        return (p.product_name || "").toLowerCase().includes(q) ||
               (p.product_family || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [addonItems, addonSearch, addonDiv]);

  const applyAddons = () => {
    const chosen = filteredAddons.filter((p) => selectedAddon[p.product_id || p.id]);
    if (chosen.length === 0) { toast.error("Pilih minimal 1 item"); return; }
    const newLines = chosen.map((p) => {
      const price = Number(p.current_price || p.promo_price || p.normal_price || 0);
      const divShort = divisionById(p.division)?.short || p.division || "ADD";
      return {
        description: `[${divShort}] ${p.product_name}${p.short_description ? ` — ${p.short_description}` : ""}`,
        quantity: 1, unit_price: price,
        source: "addon", division_id: p.division || "", product_id: p.product_id,
      };
    });
    setForm((f) => ({ ...f, lines: [...f.lines, ...newLines] }));
    setAddonOpen(false);
    toast.success(`${chosen.length} add-on ditambahkan`);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        discount: Number(form.discount),
        lines: form.lines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          subtotal: Number(l.quantity) * Number(l.unit_price),
        })).filter((l) => l.description),
      };
      await api.post("/quotations", payload);
      toast.success("Penawaran dibuat");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Penawaran / Quotation</h1>
          <p className="text-sm text-[#5C6066] mt-1">Nomor: <span className="font-mono">QTN-DWG-YYYY-XXXX</span></p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand text-white" data-testid="btn-add-quotation"><Plus className="w-4 h-4 mr-1.5" /> Penawaran Baru</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Buat Penawaran</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Klien</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="qtn-client-select"><SelectValue placeholder="Pilih klien" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{clientName(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Nama Acara</Label><Input className="mt-1.5" value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} data-testid="qtn-event-name" /></div>
                <div><Label>Tanggal Acara</Label><Input type="date" className="mt-1.5" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
                <div><Label>Venue</Label><Input className="mt-1.5" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></div>
                <div><Label>Lokasi</Label><Input className="mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div><Label>Berlaku Sampai</Label><Input type="date" className="mt-1.5" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
              </div>

              <div>
                <Label>Divisi Terlibat</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {DIVISIONS.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 p-2 rounded border border-[#E5E0D8] bg-white cursor-pointer hover:bg-[#FAF6EE]" data-testid={`qtn-div-${d.id}`}>
                      <Checkbox checked={form.division_ids.includes(d.id)} onCheckedChange={() => toggleDiv(d.id)} />
                      <div><div className="text-xs font-medium">{d.short}</div></div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Package DWG banner */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F0EBE0]">
                <Label className="mb-0">Item Penawaran</Label>
                <Button variant="outline" size="sm" className="h-8 border-[#B7A175] text-[#B7A175] hover:bg-[#FAF6EE]" onClick={openPkgModal} data-testid="btn-autofill-package">
                  <Sparkles className="w-3.5 h-3.5 mr-1"/>Pilih Paket DWG
                </Button>
              </div>

              {packageMode && (
                <div className="p-3 rounded-md border border-[#E4D9BE] bg-[#FBF5E5] text-xs text-[#7A5F1A]" data-testid="package-mode-banner">
                  <Package className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
                  <b>Mode Paket DWG aktif</b> — semua vendor terlibat sudah termasuk dalam paket. Daftar harga per vendor disembunyikan. Add-on tambahan masih bisa ditambahkan.
                </div>
              )}

              {/* Per-Division Pricelist (hidden in package mode) */}
              {!packageMode && form.division_ids.length > 0 && (
                <div className="space-y-2" data-testid="per-div-pricelist">
                  <div className="text-[11px] uppercase tracking-wider text-[#8C9096] font-semibold">Harga Satuan per Vendor Divisi</div>
                  {form.division_ids.map((did) => {
                    const list = divPricelist[did] || [];
                    return (
                      <div key={did} className="p-2 rounded-md border border-[#E5E0D8] bg-white">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-[#B7A175] min-w-[70px]">{divisionById(did)?.short || did}</span>
                          <Select value="" onValueChange={(pid) => {
                            const p = list.find((x) => (x.product_id || x.id) === pid);
                            if (p) addPricelistItem(p, did);
                          }}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`div-pricelist-${did}`}>
                              <SelectValue placeholder={list.length ? `Pilih dari ${list.length} item…` : "Belum ada item pricelist"} />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                              {list.map((p) => {
                                const pid = p.product_id || p.id;
                                const price = Number(p.current_price || p.promo_price || p.normal_price || 0);
                                return (
                                  <SelectItem key={pid} value={pid}>
                                    <div className="flex justify-between gap-3 min-w-0 w-full">
                                      <span className="truncate">{p.product_name}</span>
                                      <span className="font-mono text-[#B7A175] shrink-0">{formatIDRShort(price)}</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Lines */}
              <div className="space-y-2">
                {form.lines.length === 0 && (
                  <div className="text-center py-6 text-xs text-[#8C9096] border border-dashed border-[#E5E0D8] rounded-md">
                    Belum ada item. Pilih Paket DWG, pilih dari pricelist per divisi, atau tambah manual/add-on.
                  </div>
                )}
                {form.lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`qtn-line-${i}`}>
                    <div className="col-span-6">
                      <Input placeholder="Deskripsi" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
                      {l.source && l.source !== "manual" && (
                        <span className={`inline-block mt-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ${
                          l.source === "dwg_package" ? "bg-[#FBF5E5] text-[#7A5F1A]" :
                          l.source === "division_item" ? "bg-sky-50 text-sky-700" :
                          l.source === "addon" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"
                        }`}>
                          {l.source === "dwg_package" ? "Paket DWG" : l.source === "division_item" ? "Item Divisi" : l.source === "addon" ? "Add-on" : l.source}
                        </span>
                      )}
                    </div>
                    <Input className="col-span-2" type="number" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                    <Input className="col-span-3" type="number" placeholder="Harga" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeLine(i)} data-testid={`qtn-line-del-${i}`}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={openAddonModal} data-testid="btn-add-addon">
                    <PlusCircle className="w-4 h-4 mr-1" /> Tambah Add-on dari Pricelist
                  </Button>
                  <Button variant="ghost" size="sm" onClick={addManualLine} data-testid="btn-add-manual">
                    <Plus className="w-4 h-4 mr-1" /> Item Manual
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#F0EBE0]">
                <div><Label>Diskon (Rp)</Label><Input type="number" className="mt-1.5" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></div>
                <div className="text-right pt-6">
                  <div className="text-xs text-[#8C9096]">Subtotal: <span className="font-mono">{formatIDR(subtotal)}</span></div>
                  <div className="text-lg font-display font-bold">Total: {formatIDR(total)}</div>
                </div>
              </div>
              <div><Label>Syarat & Ketentuan</Label><Textarea rows={2} className="mt-1.5" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></div>
              <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-brand text-white" onClick={submit} disabled={busy || !form.client_id || !form.event_date} data-testid="btn-save-quotation">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Kode</th>
                <th className="text-left px-4 py-3">Klien</th>
                <th className="text-left px-4 py-3">Acara</th>
                <th className="text-left px-4 py-3">Tanggal</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8C9096]">Belum ada penawaran.</td></tr>}
              {rows.map((q) => (
                <tr key={q.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`quotation-row-${q.quotation_code}`}>
                  <td className="px-5 py-3"><Link to={`/quotations/${q.id}`} className="font-mono text-xs text-brand hover:underline">{q.quotation_code}</Link></td>
                  <td className="px-4 py-3">{clientName(q.client)}</td>
                  <td className="px-4 py-3">{q.event_name}</td>
                  <td className="px-4 py-3">{formatDate(q.event_date)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatIDRShort(q.final_total)}</td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} label={QUOTATION_STATUS_LABEL[q.status]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-[#F0EBE0]">
          {rows.length === 0 && <div className="p-8 text-center text-[#8C9096] text-sm">Belum ada penawaran.</div>}
          {rows.map((q) => (
            <Link key={q.id} to={`/quotations/${q.id}`} className="block p-4 hover:bg-[#F7F4EE]">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-mono text-xs text-brand">{q.quotation_code}</div>
                  <div className="font-medium mt-1">{clientName(q.client)}</div>
                  <div className="text-xs text-[#5C6066]">{q.event_name}</div>
                </div>
                <StatusBadge status={q.status} label={QUOTATION_STATUS_LABEL[q.status]} />
              </div>
              <div className="mt-2 flex justify-between text-xs text-[#5C6066]">
                <span>{formatDate(q.event_date)}</span>
                <span className="font-mono font-semibold">{formatIDRShort(q.final_total)}</span>
              </div>
            </Link>
          ))}
        </div>
      </Card>

      {/* Auto-fill Paket DWG modal */}
      <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="pkg-modal">
          <DialogHeader><DialogTitle>Pilih Paket DWG (Bundling)</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 pb-2 border-b border-[#E5E0D8]">
            <Label className="text-xs whitespace-nowrap">Sortir dekat harga (Rp)</Label>
            <Input type="number" placeholder="10000000" className="h-8 w-40" value={targetPrice} onChange={(e) => setTargetPrice(e.target.value)} data-testid="pkg-target-price" />
            <Button size="sm" variant="outline" onClick={openPkgModal} data-testid="pkg-reload">Muat Ulang</Button>
            <div className="ml-auto text-xs text-[#8C9096]">{Object.values(selectedPkg).filter(Boolean).length} dipilih</div>
          </div>
          <div className="flex-1 overflow-y-auto py-2 space-y-2">
            {packages.length === 0 && <div className="text-center py-10 text-sm text-[#8C9096]">Belum ada paket DWG (bundling). Tandai brand paket dengan 'DWG All-in' atau '+' di Master Pricelist.</div>}
            {packages.slice(0, 60).map((p) => {
              const pid = p.product_id || p.id;
              const price = Number(p.current_price || p.promo_price || p.normal_price || 0);
              const inclDivs = (p.included_divisions && p.included_divisions.length ? p.included_divisions : (p.division ? [p.division] : []));
              return (
                <label key={pid} className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-[#FAF6EE] ${selectedPkg[pid] ? "border-[#B7A175] bg-[#FAF6EE]" : "border-[#E5E0D8]"}`} data-testid={`pkg-${pid}`}>
                  <Checkbox checked={!!selectedPkg[pid]} onCheckedChange={(v) => setSelectedPkg((sp) => ({ ...sp, [pid]: !!v }))} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{p.product_name}</span>
                      <span className="font-mono text-xs text-[#B7A175]">{formatIDR(price)}</span>
                    </div>
                    <div className="text-[11px] text-[#8C9096] truncate">{p.short_description || p.product_family || "-"}</div>
                    {inclDivs.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {inclDivs.slice(0, 8).map((d) => (
                          <span key={d} className="text-[9px] px-1 py-0.5 rounded font-semibold bg-white border border-[#E5E0D8] text-[#5C6066]">{divisionById(d)?.short || d}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPkgOpen(false)}>Batal</Button>
            <Button onClick={applyPackage} className="bg-[#B7A175] hover:bg-[#A08A5D] text-white" data-testid="pkg-apply">Tambahkan Paket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add-on modal (bundling=false) */}
      <Dialog open={addonOpen} onOpenChange={setAddonOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="addon-modal">
          <DialogHeader><DialogTitle>Tambah dari Pricelist Add-on / Item Satuan</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-[#E5E0D8]">
            <Input placeholder="Cari item…" className="h-8 flex-1 min-w-[160px]" value={addonSearch} onChange={(e) => setAddonSearch(e.target.value)} data-testid="addon-search" />
            <Select value={addonDiv} onValueChange={setAddonDiv}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="addon-div-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Divisi</SelectItem>
                {DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-[#8C9096]">{Object.values(selectedAddon).filter(Boolean).length} dipilih</div>
          </div>
          <div className="flex-1 overflow-y-auto py-2 space-y-1.5">
            {filteredAddons.length === 0 && <div className="text-center py-10 text-sm text-[#8C9096]">Tidak ada item yang cocok.</div>}
            {filteredAddons.slice(0, 100).map((p) => {
              const pid = p.product_id || p.id;
              const price = Number(p.current_price || p.promo_price || p.normal_price || 0);
              return (
                <label key={pid} className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer hover:bg-[#FAF6EE] ${selectedAddon[pid] ? "border-[#B7A175] bg-[#FAF6EE]" : "border-[#E5E0D8]"}`} data-testid={`addon-${pid}`}>
                  <Checkbox checked={!!selectedAddon[pid]} onCheckedChange={(v) => setSelectedAddon((sp) => ({ ...sp, [pid]: !!v }))} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{p.product_name}</span>
                      <span className="font-mono text-xs text-[#B7A175]">{formatIDR(price)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] uppercase px-1 py-0.5 rounded font-semibold bg-white border border-[#E5E0D8] text-[#5C6066]">{divisionById(p.division)?.short || p.division}</span>
                      <span className="text-[10px] text-[#8C9096] truncate">{p.short_description || p.product_family || "-"}</span>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddonOpen(false)}>Batal</Button>
            <Button onClick={applyAddons} className="bg-[#B7A175] hover:bg-[#A08A5D] text-white" data-testid="addon-apply">Tambahkan ke Penawaran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
